import { useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const TIP_OPTIONS = [0, 1, 2, 3, 5];

export default function RatingModal({ delivery, userRole, token, onClose, onSubmit }) {
  const [rating, setRating] = useState(0);
  const [hovered, setHovered] = useState(0);
  const [comment, setComment] = useState('');
  const [tip, setTip] = useState(0);
  const [customTip, setCustomTip] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);

  const isRestaurant = userRole === 'restaurant';
  const targetName = isRestaurant ? delivery?.driver?.name || 'your driver' : delivery?.restaurant?.name || 'the restaurant';
  const finalTip = customTip ? parseFloat(customTip) : tip;

  async function handleSubmit() {
    if (rating === 0) return setError('Please select a rating');
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API_URL}/api/ratings/delivery/${delivery._id}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ rating, comment, ...(isRestaurant && finalTip > 0 ? { tip: finalTip } : {}) }),
      });
      const data = await res.json();
      if (!res.ok) setError(data.error || 'Rating failed');
      else { setDone(true); setTimeout(() => { onSubmit && onSubmit(); onClose(); }, 2000); }
    } catch (err) { setError('Something went wrong.'); }
    finally { setLoading(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-800 rounded-2xl p-6 w-full max-w-md">
        {done ? (
          <div className="text-center py-4">
            <div className="text-5xl mb-3">🌟</div>
            <h2 className="text-xl font-bold text-white mb-2">Thanks for rating!</h2>
            <p className="text-gray-400 text-sm">Your feedback helps make Starfuel better.</p>
          </div>
        ) : (
          <>
            <div className="flex items-center justify-between mb-5">
              <div>
                <h2 className="text-lg font-bold text-white">{isRestaurant ? 'Rate Your Driver' : 'Rate This Restaurant'}</h2>
                <p className="text-gray-400 text-sm mt-0.5">How was your experience with {targetName}?</p>
              </div>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-gray-800 hover:bg-gray-700 flex items-center justify-center text-gray-400 transition">✕</button>
            </div>

            <div className="flex justify-center gap-2 mb-3">
              {[1,2,3,4,5].map(star => (
                <button key={star} onClick={() => setRating(star)} onMouseEnter={() => setHovered(star)} onMouseLeave={() => setHovered(0)} className="text-4xl transition-transform hover:scale-110 active:scale-95">
                  <span className={star <= (hovered || rating) ? 'text-yellow-400' : 'text-gray-700'}>★</span>
                </button>
              ))}
            </div>

            {(hovered || rating) > 0 && (
              <p className="text-center text-sm text-gray-400 mb-4">{['','Poor','Fair','Good','Great','Excellent!'][hovered || rating]}</p>
            )}

            {isRestaurant && (
              <div className="mb-5">
                <p className="text-sm font-medium text-gray-300 mb-3">Add a tip? <span className="text-gray-500">(100% goes to driver)</span></p>
                <div className="flex gap-2 flex-wrap">
                  {TIP_OPTIONS.map(t => (
                    <button key={t} onClick={() => { setTip(t); setCustomTip(''); }}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${tip === t && !customTip ? 'bg-yellow-400 text-black' : 'bg-gray-800 text-gray-400 hover:bg-gray-700'}`}>
                      {t === 0 ? 'No tip' : `$${t}`}
                    </button>
                  ))}
                  <div className="flex items-center gap-1">
                    <span className="text-gray-400 text-sm">$</span>
                    <input type="number" value={customTip} onChange={e => { setCustomTip(e.target.value); setTip(0); }} placeholder="Other" min="0" max="100"
                      className="w-20 px-2 py-2 bg-gray-800 border border-gray-700 rounded-xl text-white text-sm focus:outline-none focus:border-yellow-400 transition" />
                  </div>
                </div>
                {finalTip > 0 && <p className="text-xs text-green-400 mt-2">✓ ${finalTip.toFixed(2)} tip added to driver earnings</p>}
              </div>
            )}

            <div className="mb-5">
              <label className="block text-xs text-gray-400 mb-1.5">Comment <span className="text-gray-600">(optional)</span></label>
              <textarea value={comment} onChange={e => setComment(e.target.value)} rows={3} maxLength={500}
                className="w-full px-3 py-2.5 bg-black border border-gray-700 rounded-xl text-white text-sm placeholder-gray-600 focus:outline-none focus:border-yellow-400 transition resize-none"
                placeholder={isRestaurant ? 'Great driver, very professional...' : 'Order was ready on time...'} />
            </div>

            {error && <div className="bg-red-500/10 border border-red-500/30 rounded-xl px-4 py-3 text-red-400 text-sm mb-4">{error}</div>}

            <div className="flex gap-3">
              <button onClick={onClose} className="flex-1 py-3 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition text-sm">Skip</button>
              <button onClick={handleSubmit} disabled={loading || rating === 0} className="flex-1 py-3 bg-yellow-400 hover:bg-yellow-300 text-black font-bold rounded-xl transition disabled:opacity-50 text-sm">
                {loading ? 'Submitting...' : `Submit${finalTip > 0 ? ` + $${finalTip.toFixed(2)} tip` : ''}`}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
